import Button from "components/common/Button";
import Input from "components/common/Input";
import api from "config/api";
import { useState } from "react";
import { toast } from "react-toastify";

type Props = {
  value: number;
  onSubmit: (v: string) => void;
};

const inititalData = {
  nameOnCard: "",
  cardNumber: "",
  expiryDate: "",
  cardCVC: "",
};

const formatExpiryInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const Clover = ({ value, onSubmit }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(inititalData);
  const fieldSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      background: "linear-gradient(180deg, rgba(248,250,252,.94), rgba(255,255,255,1))",
      boxShadow: "0 8px 18px rgba(15,23,42,.04)",
      "& fieldset": {
        borderColor: "rgba(15,23,42,.10)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(15,23,42,.18)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 4px rgba(15,23,42,.06)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(15,23,42,.24)",
      },
    },
    "& .MuiInputBase-input": {
      padding: "14px 16px",
      fontSize: "15px",
    },
    "& .MuiInputLabel-root": {
      color: "#667085",
      fontSize: "13px",
      fontWeight: 700,
    },
  };

  const handleSubmit = () => {
    toast.dismiss();
    if (!data.nameOnCard) {
      toast.error("Invalid Cardholder Name");
      return;
    }
    if (data.cardNumber.length !== 16) {
      toast.error("Invalid Card Number");
      return;
    }
    if (
      !data.expiryDate ||
      !/^(0[1-9]|1[0-2])\/([0-9]{4})$/.test(data.expiryDate)
    ) {
      toast.error("Invalid Expiry Date");
      return;
    }
    if (!/^\d+$/.test(data.cardCVC) || data.cardCVC.length !== 3) {
      toast.error("Invalid Card CVV");
      return;
    }
    setLoading(true);
    api
      .post("/api/Clover", { ...data, amount: value })
      .then(({ data }) => {
        onSubmit("CLOVER");
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="mt-2 max-w-[760px]">
      <div className="flex flex-col gap-3">
      <Input
        size="small"
        type="text"
        required
        label="Cardholder Name"
        value={data.nameOnCard}
        sx={fieldSx}
        onValueChange={(nameOnCard) =>
          setData((e) => ({ ...e, nameOnCard: nameOnCard.toUpperCase() }))
        }
      />
      <Input
        size="small"
        type="text"
        required
        label="Card Number"
        placeholder="0000 0000 0000 0000"
        value={data.cardNumber}
        sx={fieldSx}
        onValueChange={(cardNumber) =>
          setData((e) => ({
            ...e,
            cardNumber: String(cardNumber || "").replace(/\D/g, "").slice(0, 16),
          }))
        }
      />
      <div className="flex gap-2">
        <Input
          size="small"
          type="text"
          required
          className="flex-1"
          placeholder="MM/YYYY"
          label="Expiration"
          value={data.expiryDate}
          sx={fieldSx}
          onValueChange={(expiryDate) =>
            setData((e) => ({ ...e, expiryDate: formatExpiryInput(String(expiryDate || "")) }))
          }
        />
        <Input
          size="small"
          type="text"
          required
          label="CVV"
          className="flex-1"
          value={data.cardCVC}
          sx={fieldSx}
          onValueChange={(cardCVC) =>
            setData((e) => ({
              ...e,
              cardCVC: String(cardCVC || "").replace(/\D/g, "").slice(0, 3),
            }))
          }
        />
      </div>
      <p className="text-[12px] text-[#667085] -mt-1">
        Expiration format: MM/YYYY
      </p>
      <div className="pt-1">
        <Button
          disabled={loading}
          onClick={handleSubmit}
          className="!min-h-[46px] !px-8 !rounded-full !text-[15px] !font-[800] !tracking-[-0.01em] !shadow-lg !w-full sm:!w-auto sm:!min-w-[180px]"
        >
          Pay Now
        </Button>
      </div>
      </div>
    </div>
  );
};

export default Clover;
